import { store } from "../../store";
import { controlLoading, controlTabBar } from "../../slices/app.slice";
import { config } from "../../../config";
import { GetAccessTokenAPI } from "./accessToken";
import { autoLogin, authenticate } from "../../slices/auth.slice";
import * as service from "../service";

export interface BusinessRegisterPayload {
  username: string;
  company_email: string;
  phone_num: string;
  password: string;
  company_name: string;
  br_number: string;
  br_image: string;
  company_address: string;
  profile_icon: string;
  plan_type?: "monthly" | "yearly";
  terms_accepted?: boolean;
  terms_version?: string;
  checkout_session_id?: string;
}

export const sendBusinessEmailVerificationCodeAPI = async (
  username: string,
  companyEmail: string,
) => {
  try {
    const resp = await service.call(
      "GET",
      "api/user/business/signup/sendVerficationCode",
      {
        email: companyEmail,
        username,
      },
      { timeout: 20000 },
    );
    return resp;
  } catch (err) {
    store.dispatch(controlLoading({ showLoading: false }));
    console.error(`sendBusinessEmailVerificationCodeAPI ${err}`);
  }
};

export const resendBusinessEmailVerificationCodeAPI = async (
  companyEmail: string,
  username: string,
) => {
  if (!companyEmail || !companyEmail.trim() || !username || !username.trim()) {
    console.error(
      `resendBusinessEmailVerificationCodeAPI aborted: email and username are required, received email ${JSON.stringify(
        companyEmail,
      )}, username ${JSON.stringify(username)}`,
    );
    return { result: "Email is required", status: 2 };
  }
  try {
    const resp = await service.call(
      "GET",
      "api/user/business/signup/sendVerficationCode",
      {
        email: companyEmail,
        username,
      },
      { timeout: 20000 },
    );
    return resp;
  } catch (err) {
    store.dispatch(controlLoading({ showLoading: false }));
    console.error(`resendBusinessEmailVerificationCodeAPI ${err}`);
  }
};

export const verifyBusinessEmailCodeAPI = async (
  companyEmail: string,
  code: string,
) => {
  if (!companyEmail || !companyEmail.trim() || !code || !code.trim()) {
    return { result: "Email and verification code are required", status: 2 };
  }
  try {
    return await service.call(
      "POST",
      "api/user/business/signup/verifyEmailCode",
      { email: companyEmail, code },
      { timeout: 20000 },
    );
  } catch (err) {
    store.dispatch(controlLoading({ showLoading: false }));
    console.error(`verifyBusinessEmailCodeAPI ${err}`);
  }
};

export const getBusinessSignupPromoAPI = async () => {
  try {
    return await service.call(
      "GET",
      "api/user/business/signup/promo",
      {},
      { timeout: 20000 },
    );
  } catch (err) {
    store.dispatch(controlLoading({ showLoading: false }));
    console.error(`getBusinessSignupPromoAPI ${err}`);
  }
};

export const sendBusinessPhoneVerificationCodeAPI = async (
  phone: string,
  regionCode: string,
) => {
  if (!phone || !phone.trim()) {
    return { result: "Phone number is required", status: 2 };
  }
  try {
    return await service.call(
      "GET",
      "api/user/business/signup/sendPhoneVerificationCode",
      { phone, regionCode },
      { timeout: 20000 },
    );
  } catch (err) {
    store.dispatch(controlLoading({ showLoading: false }));
    console.error(`sendBusinessPhoneVerificationCodeAPI ${err}`);
  }
};

export const verifyBusinessPhoneCodeAPI = async (
  phone: string,
  regionCode: string,
  code: string,
) => {
  if (!phone || !phone.trim() || !code || !code.trim()) {
    return { result: "Phone number and verification code are required", status: 2 };
  }
  try {
    return await service.call(
      "POST",
      "api/user/business/signup/verifyPhoneCode",
      { phone, regionCode, code },
      { timeout: 20000 },
    );
  } catch (err) {
    store.dispatch(controlLoading({ showLoading: false }));
    console.error(`verifyBusinessPhoneCodeAPI ${err}`);
  }
};

export const BusinessRegisterAPI = async (payload: BusinessRegisterPayload) => {
  try {
    const resp = await service.call(
      "POST",
      "api/user/business/signup/register",
      payload,
      { timeout: 60000 },
    );

    if (resp.status === 0) {
      const userAuth = resp.result?.user_auth ?? resp.result?.role;
      const normalizedBusiness = {
        id: resp.result.id,
        user_id: resp.result.user_id,
        br_image: resp.result.br_image,
        br_number: resp.result.br_number,
        company_address: resp.result.company_address,
        company_email: resp.result.company_email,
        company_name: resp.result.company_name,
        contact_number: resp.result.contact_number,
        user_auth: userAuth,
        utime: resp.result.utime,
        ctime: resp.result.ctime,
      };
      if (resp.result.review_status === "approved") {
        const accessToken = await GetAccessTokenAPI(
          resp.result.user_id,
          resp.result.company_email,
        );
        store.dispatch(
          autoLogin({
            userType: "business",
            user: normalizedBusiness,
            access_token: accessToken,
          }),
        );
        config.ACCESS_TOKEN = accessToken;
        store.dispatch(authenticate({ loggedIn: true, user_auth: userAuth }));
        store.dispatch(
          controlTabBar({
            home: false,
            calendar: false,
            survey: true,
            activtiy: false,
            redeem: false,
            qrcodeScan: false,
            chat: false,
            profile: false,
          }),
        );
      }
      store.dispatch(controlLoading({ showLoading: false }));
      return resp;
    }

    store.dispatch(controlLoading({ showLoading: false }));
    return resp;
  } catch (err) {
    store.dispatch(controlLoading({ showLoading: false }));
    console.error(`BusinessRegisterAPI ${err}`);
  }
};

export const CreateBusinessSignupCheckoutAPI = async (payload: {
  username: string;
  company_email: string;
  phone_num: string;
  password: string;
  company_name: string;
  br_number: string;
  br_image: string;
  company_address: string;
  profile_icon: string;
  plan_type: "monthly" | "yearly";
  return_path?: string;
}) => {
  try {
    const resp = await service.call(
      "POST",
      "api/user/business/signup/createCheckout",
      payload,
      { timeout: 60000 },
    );
    return resp;
  } catch (err) {
    store.dispatch(controlLoading({ showLoading: false }));
    console.error(`CreateBusinessSignupCheckoutAPI ${err}`);
  }
};
